import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Clock, DollarSign, FileText, CheckCircle, MapPin, Banknote, Languages, Globe, ArrowRight } from "lucide-react";
import { useCurrency } from "../context/CurrencyContext";
import { useSEO, addJsonLd } from "../hooks/useSEO";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CountryPage() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [country, setCountry] = useState(null);
  const [pricing, setPricing] = useState([]);
  const { convert } = useCurrency();

  useEffect(() => {
    axios.get(`${API}/countries/${code}`).then(r => setCountry(r.data)).catch(() => navigate("/"));
    axios.get(`${API}/pricing?destination=${code}&visa_type=tourist&processing_type=standard`).then(r => setPricing(r.data.all_options || [])).catch(() => {});
  }, [code, navigate]);

  // SEO: Dynamic meta + schema
  useSEO({
    title: country ? `Apply ${country.country_name} Visa Online | Price $${country.base_price} | FlyInVisa` : "Loading...",
    description: country ? `Get ${country.country_name} visa. Processing: 5-15 days. Documents: ${country.required_docs?.slice(0, 3).join(", ")}. Apply online on FlyInVisa.` : "",
    keywords: country ? [`${country.country_name} visa`, `${country.country_name} visa price`, `apply ${country.country_name} visa online`, `${country.country_name} visa requirements`] : []
  });
  useEffect(() => {
    if (country) addJsonLd({ "@context": "https://schema.org", "@type": "Product", "name": `${country.country_name} Visa`, "description": country.description, "offers": { "@type": "Offer", "price": String(country.base_price), "priceCurrency": "USD" }, "provider": { "@type": "Organization", "name": "FlyInVisa" } });
  }, [country]);

  if (!country) return <div className="min-h-screen flex items-center justify-center bg-slate-50" ><div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-slate-50" >
      {/* Hero */}
      <div className="relative h-72 md:h-96 overflow-hidden">
        <img src={country.image} alt={country.country_name} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#111111] via-[#111111]/50 to-transparent" />
        <div className="absolute bottom-8 left-8">
          <button onClick={() => navigate("/")} className="flex items-center gap-2 text-sm text-slate-900/70 hover:text-slate-900 mb-4"><ArrowLeft className="w-4 h-4" /> Back</button>
          <div className="flex items-center gap-3">
            <span className="text-4xl">{country.flag}</span>
            <div>
              <h1 className="heading-font text-3xl md:text-4xl font-bold text-slate-900">{country.country_name}</h1>
              <p className="text-sm text-blue-600 mt-1">{country.visa_types?.map(v => v.charAt(0).toUpperCase() + v.slice(1)).join(", ")} Visas</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-12">
        {/* Info Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          {[
            { icon: MapPin, label: "Capital", value: country.capital },
            { icon: Banknote, label: "Currency", value: country.currency },
            { icon: Languages, label: "Language", value: country.language },
            { icon: Globe, label: "Continent", value: country.continent },
          ].map(i => (
            <div key={i.label} className="card-elevated rounded-xl p-4">
              <div className="flex items-center gap-2 mb-1"><i.icon className="w-4 h-4 text-blue-600" /><span className="text-xs text-slate-400">{i.label}</span></div>
              <span className="text-sm text-slate-900 font-medium">{i.value}</span>
            </div>
          ))}
        </div>

        <p className="text-slate-500 text-sm leading-relaxed mb-10">{country.description}</p>

        {/* Pricing Options */}
        {pricing.length > 0 && (
          <div className="mb-12">
            <h2 className="heading-font text-xl font-semibold text-slate-900 mb-6">Visa Pricing</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {pricing.map(p => (
                <div key={p.processing_type} className={`card-elevated rounded-2xl p-6 ${p.processing_type === "express" ? "border-blue-300 bg-blue-50" : ""}`}>
                  <span className="text-xs text-slate-400 uppercase">{p.processing_type}</span>
                  <div className="text-2xl font-bold text-slate-900 heading-font mt-1">{convert(p.total || p.price + 29)}</div>
                  <div className="space-y-2 mt-3">
                    <div className="flex items-center gap-2 text-sm text-slate-500"><Clock className="w-3.5 h-3.5" />{p.processing_time_days} days</div>
                    <div className="flex items-center gap-2 text-sm text-slate-500"><CheckCircle className="w-3.5 h-3.5 text-emerald-600" />{p.approval_rate}% approval</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Required Documents - Interactive Checklist */}
        {country.required_docs && (
          <div className="mb-12">
            <h2 className="heading-font text-xl font-semibold text-slate-900 mb-4">Required Documents</h2>
            <div className="space-y-2">
              {country.required_docs.map(d => (
                <label key={d} className="flex items-center gap-3 p-3 bg-white rounded-xl border border-slate-100 hover:border-blue-200 cursor-pointer transition-all group">
                  <input type="checkbox" className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
                  <FileText className="w-4 h-4 text-slate-400 group-hover:text-blue-500" />
                  <span className="text-sm text-slate-700">{d}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Application Process Steps */}
        <div className="mb-12">
          <h2 className="heading-font text-xl font-semibold text-slate-900 mb-4">Application Process</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[{ step: "1", title: "Fill Application", desc: "Enter personal & travel details" },
              { step: "2", title: "Upload Documents", desc: "Submit required documents" },
              { step: "3", title: "Make Payment", desc: "Secure online payment" },
              { step: "4", title: "Track Status", desc: "Real-time application tracking" }
            ].map(s => (
              <div key={s.step} className="card-elevated p-4 text-center">
                <div className="w-8 h-8 rounded-full bg-blue-600 text-white text-sm font-bold flex items-center justify-center mx-auto mb-2">{s.step}</div>
                <p className="text-sm font-semibold text-slate-800">{s.title}</p>
                <p className="text-xs text-slate-400 mt-1">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Trust Indicators */}
        <div className="mb-12 grid grid-cols-3 gap-4">
          {[{ icon: "shield", title: "Secure Application", desc: "256-bit encryption" },
            { icon: "zap", title: "Fast Processing", desc: "As quick as 24 hours" },
            { icon: "check", title: "Trusted Platform", desc: "10,000+ visas processed" }
          ].map(t => (
            <div key={t.title} className="text-center p-4 bg-blue-50 rounded-xl border border-blue-100">
              <p className="text-sm font-semibold text-blue-700">{t.title}</p>
              <p className="text-xs text-blue-500 mt-0.5">{t.desc}</p>
            </div>
          ))}
        </div>


        {/* Highlights */}
        {country.highlights && (
          <div className="mb-12">
            <h2 className="heading-font text-xl font-semibold text-slate-900 mb-4">Highlights</h2>
            <ul className="space-y-2">
              {country.highlights.map(h => (
                <li key={h} className="flex items-center gap-2 text-sm text-slate-500"><div className="w-1.5 h-1.5 rounded-full bg-blue-600" />{h}</li>
              ))}
            </ul>
          </div>
        )}

        <button onClick={() => navigate(`/pricing?destination=${code}`)} data-testid="country-apply-btn"
          className="w-full md:w-auto py-3.5 px-10 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition-all flex items-center justify-center gap-2">
          Check Pricing & Apply <ArrowRight className="w-4 h-4" />
        </button>
      </div>

      {/* Sticky Apply Now button (mobile) */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-white/90 backdrop-blur border-t border-slate-200 md:hidden z-40">
        <button onClick={() => navigate(`/pricing?destination=${code}`)} data-testid="sticky-apply-btn"
          className="w-full py-3.5 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 flex items-center justify-center gap-2 shadow-lg">
          Apply Now - {convert(country.base_price)} <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
